// Phase 43.55/59 — Blocco "Corse mensili esaurite"
// Riordinato in 43.59: PACCHETTO +10 ora primario (CTR maggiore osservato).
// Upgrade secondario, Singola corsa terziario ghost.
// Pack: "12 mesi · paga una volta · nessun rinnovo · consumo automatico"
import { Sparkles, Package, ArrowRight, Zap, CheckCircle2 } from "lucide-react";
import { formatCHF } from "@/lib/format";
import { validPriceTax } from "@/lib/priceTax";
import CustomerTaxBreakdown from "@/components/CustomerTaxBreakdown";

export default function OutOfRidesBlock({
  currentPlan,
  upgradePlan,
  packPriceChf = null,
  packCatalog = null,
  business = false,
  paymentsDisabled = false,
  onUpgrade,
  onBuyPack,
  onSingleRide,
  packBusy = false,
}) {
  if (!currentPlan) return null;
  const currentCount = currentPlan.included_deliveries_monthly || 0;
  const packTax = validPriceTax(packCatalog?.price_tax) ? packCatalog.price_tax : null;
  const packTotal = packTax?.total_chf ?? packCatalog?.price_chf ?? packPriceChf;
  const packAvailable = Number.isFinite(packTotal) && (business ? packTax?.audience === "business" : (!packTax || packTax.audience === "private"));
  const upgradeTax = validPriceTax(upgradePlan?.price_tax) ? upgradePlan.price_tax : null;
  const upgradeAvailable = Boolean(upgradePlan) && (!business || upgradeTax?.audience === "business");
  const upgradeTotal = upgradeTax?.total_chf ?? upgradePlan?.price_chf;
  return (
    <section
      data-testid="out-of-rides-block"
      className="relative rounded-3xl border border-dapnow-line bg-gradient-to-br from-emerald-50/40 via-white to-white p-5 sm:p-7 space-y-5 shadow-sm"
    >
      <header className="flex items-start gap-3">
        <div className="shrink-0 rounded-2xl bg-dapnow-blue/10 p-3 ring-1 ring-dapnow-blue/20">
          <Zap className="h-6 w-6 text-dapnow-blue" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="overline text-dapnow-blue/90">Piano · Stato corse</p>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tighter text-dapnow-ink mt-1">
            Le tue corse mensili sono terminate
          </h2>
          <p className="text-sm text-dapnow-muted mt-1.5">
            Scegli come continuare. Il <strong>Pacchetto extra</strong> ti dà flessibilità senza rinnovi automatici.
          </p>
        </div>
      </header>

      {/* ── Confronto rapido ─────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
        <ComparisonTile
          label="Piano attuale"
          value={`${currentCount} corse`}
          sub="terminate"
          tone="muted"
          testid="comparison-current"
        />
        <ComparisonTile
          label="Pacchetto extra · 10 corse"
          value="+10 corse"
          sub={`${packAvailable ? formatCHF(packTotal) : "—"} IVA inclusa · valido 12 mesi`}
          tone="emerald-strong"
          testid="comparison-pack"
        />
        {upgradePlan && (
          <ComparisonTile
            label={`Upgrade · ${upgradePlan.name}`}
            value={`${upgradePlan.included_deliveries_monthly || 0} corse/mese`}
            sub={`${upgradeAvailable ? formatCHF(upgradeTotal) : "—"}/mese IVA inclusa · rinnovo automatico`}
            tone="primary"
            testid="comparison-upgrade"
          />
        )}
      </div>

      {/* ── CTA PRIMARIO: PACCHETTO EXTRA (43.59 — promosso a primario) ── */}
      <button
        type="button"
        onClick={onBuyPack}
        disabled={packBusy || paymentsDisabled || !packAvailable}
        data-testid="out-of-rides-cta-pack"
        className="group w-full flex flex-wrap items-stretch gap-3 rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-700 text-white px-4 sm:px-5 py-4 hover:from-emerald-500 hover:to-emerald-600 transition shadow-[0_20px_36px_rgba(16,185,129,0.32)] disabled:opacity-70"
      >
        <div className="shrink-0 flex items-center justify-center">
          <Package className="h-6 w-6" />
        </div>
        <div className="flex-1 text-left min-w-0">
          <p className="text-[10px] uppercase tracking-wider opacity-85 font-bold">Consigliato · Massima flessibilità</p>
          <p className="font-display text-base sm:text-lg font-bold tracking-tight">
            Pacchetto +10 corse · paga una volta
          </p>
          <ul className="text-[11px] sm:text-xs opacity-95 mt-1.5 space-y-0.5">
            <li className="flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 shrink-0" /> Valido 12 mesi</li>
            <li className="flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 shrink-0" /> Nessun rinnovo automatico</li>
            <li className="flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 shrink-0" /> Consumo automatico al prossimo ordine</li>
          </ul>
        </div>
        <div className="shrink-0 flex flex-col items-end justify-between">
          <span className="font-display text-xl sm:text-2xl font-extrabold tabular-nums leading-none">
            {packAvailable ? `Paga ${formatCHF(packTotal)}` : "Prezzo da verificare"}
          </span>
          <ArrowRight className="h-5 w-5 transition group-hover:translate-x-0.5" />
        </div>
      </button>
      {business && packAvailable && <CustomerTaxBreakdown tax={packTax} testId="out-of-rides-pack-tax" />}

      {/* ── CTA SECONDARIO: UPGRADE ──────────────────────────────────── */}
      {upgradePlan && (
        <button
          type="button"
          onClick={onUpgrade}
          disabled={paymentsDisabled || !upgradeAvailable}
          data-testid="out-of-rides-cta-upgrade"
          className="group w-full flex flex-wrap items-center gap-3 rounded-2xl border-2 border-dapnow-line bg-white px-4 sm:px-5 py-3.5 hover:border-dapnow-blue hover:bg-dapnow-blue-soft/30 transition disabled:opacity-60"
        >
          <Sparkles className="h-5 w-5 shrink-0 text-dapnow-blue" />
          <div className="flex-1 text-left min-w-0">
            <p className="font-display text-sm sm:text-base font-bold tracking-tight text-dapnow-ink">
              Upgrade a {upgradePlan.name}
            </p>
            <p className="text-xs text-dapnow-muted mt-0.5 truncate">
              {upgradePlan.included_deliveries_monthly} consegne incluse/mese · rinnovo automatico
            </p>
          </div>
          <span className="font-display text-base font-extrabold tabular-nums text-dapnow-ink shrink-0">
            {upgradeAvailable ? formatCHF(upgradeTotal) : "—"}/mese
          </span>
        </button>
      )}
      {business && upgradeAvailable && <CustomerTaxBreakdown tax={upgradeTax} testId="out-of-rides-upgrade-tax" />}

      {/* ── CTA TERZIARIO ─────────────────────────────────────────────── */}
      <button
        type="button"
        onClick={onSingleRide}
        data-testid="out-of-rides-cta-single"
        className="w-full text-center text-sm text-dapnow-muted hover:text-dapnow-ink transition underline-offset-4 hover:underline py-1"
      >
        oppure calcola il prezzo di una corsa singola
      </button>
    </section>
  );
}

function ComparisonTile({ label, value, sub, tone, testid }) {
  const palette = {
    muted: "border-dapnow-line bg-white text-dapnow-muted",
    primary: "border-dapnow-blue/30 bg-dapnow-blue-soft/60 text-dapnow-ink",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    "emerald-strong": "border-emerald-300 bg-emerald-100 text-emerald-900 ring-2 ring-emerald-200",
  }[tone] || "border-dapnow-line bg-white";
  return (
    <div className={`rounded-2xl border p-3 ${palette}`} data-testid={testid}>
      <p className="overline text-[10px] truncate">{label}</p>
      <p className="font-display text-lg sm:text-xl font-extrabold tracking-tight mt-1 tabular-nums truncate">
        {value}
      </p>
      <p className="text-[11px] mt-0.5 opacity-80">{sub}</p>
    </div>
  );
}
